import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Check for JWT token first
    const token = localStorage.getItem('subguard_token');
    const storedUser = localStorage.getItem('subguard_user');
    
    // If we have a token that's not the google_oauth marker
    if (token && token !== 'google_oauth' && storedUser) {
      try {
        const response = await authApi.getMe();
        const freshUser = response.data;
        setUser(freshUser);
        setIsAuthenticated(true);
        // Update localStorage with fresh data (includes has_quick_check)
        localStorage.setItem('subguard_user', JSON.stringify(freshUser));
        setIsLoading(false);
        return;
      } catch (error) {
        // Token invalid, clear storage
        localStorage.removeItem('subguard_token');
        localStorage.removeItem('subguard_user');
      }
    }
    
    // If we have Google OAuth marker, verify with backend
    if (token === 'google_oauth' && storedUser) {
      try {
        const response = await authApi.getMe();
        const freshUser = response.data;
        setUser(freshUser);
        setIsAuthenticated(true);
        // Update localStorage with fresh data (includes has_quick_check)
        localStorage.setItem('subguard_user', JSON.stringify(freshUser));
        setIsLoading(false);
        return;
      } catch (error) {
        // Session invalid, clear storage
        localStorage.removeItem('subguard_token');
        localStorage.removeItem('subguard_user');
      }
    }
    
    // No auth found
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
  };

  const login = (token, userData) => {
    localStorage.setItem('subguard_token', token);
    localStorage.setItem('subguard_user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('subguard_token');
    localStorage.removeItem('subguard_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('subguard_user', JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        logout,
        updateUser,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
